import sumBy from 'lodash/sumBy';

import {FlatSegment, InsightsBookmarkParams, InsightsQueryResults} from 'common/report/insights/util/types';
import {
  canShowOtherSegment,
  getFlatSegmentDataTableColumns,
  getFlatSegments,
  getFlatSegmentTableRow,
  getSeriesDisplaySegmentColumns,
  getSingleNumberFromSegmentValue,
  getSortedKeyMapForFlatSegments,
  isSingleSegmentedMetric,
} from 'common/report/insights/util';
import {
  ReadOnlySegmentItem,
  SegmentFilterController,
  SegmentItem,
} from 'common/charts/contexts/segment-filter-controller';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {Columns} from 'common/components/data-table/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';

export interface PieChartData {
  legendSegmentItems: SegmentItem[]; // sorted array of info for each segment (for use in SegmentFilterController)
  readOnlyLegendItems: ReadOnlySegmentItem[];

  // Insights uses a special index with a stable sort order (independent from the bookmark sort order) to store selected legend segments
  legendIdToIndexMap: {[id: string]: number};
  legendIndexToId: string[];

  // easy lookups for data segment to present tooltips or an aggregate "Other" segment
  segmentLookup: Map<string, FlatSegment>; // Map key is FlatSegment.key, same as SegmentItem.id in `legendSegmentItems`

  // Query API will begin to supply an overall total for a series and any breakdowns. If that data present, this will be populated.
  overallTotal?: number;
  tableColumns: Columns;
}

interface OtherSegment {
  metricKeys: string[];
  totalValue: number;
}

export function getOtherSegment(
  segmentLookup: Map<string, FlatSegment>,
  segmentFilterController: SegmentFilterController,
  overallTotal: number,
): OtherSegment {
  const unselectedSegments: FlatSegment[] = Array.from(segmentLookup.values()).filter((segment) => {
    return !segmentFilterController.isItemSelected(0, segment.key);
  });
  const unselectedMetricKeys = unselectedSegments.map((segment: FlatSegment) => segment.path[0]);

  const selectedSegments: FlatSegment[] = Array.from(segmentLookup.values()).filter((segment) => {
    return segmentFilterController.isItemSelected(0, segment.key);
  });
  const selectedTotal = sumBy(selectedSegments, (segment: FlatSegment) =>
    getSingleNumberFromSegmentValue(segment.value),
  );

  const overallRemainder = overallTotal - selectedTotal;

  return {metricKeys: unselectedMetricKeys, totalValue: overallRemainder};
}

export function getPieChartData(
  bookmarkParams: InsightsBookmarkParams,
  results: InsightsQueryResults,
  dataGroups: Record<string, DataGroup>,
): PieChartData {
  // flatten results into convenient segments
  const segments = getFlatSegments(results.headers, results.series);

  // for display, the api-provided overall segments need to be excluded. But we use them to help with the $other segment
  const segmentsToDisplay = segments.filter((segment) => !segment.path.includes(RESULTS_OVERALL_SUM_KEY));

  // create lookups based on segment key, useful for presenting tooltips or an aggregate "Other" segment
  const segmentLookup = new Map(segmentsToDisplay.map((segment) => [segment.key, segment]));

  // generate line chart series & legend info from segments
  const legendSegmentItems: SegmentItem[] = [];

  const segmentColumns = getSeriesDisplaySegmentColumns(bookmarkParams.sections, results, dataGroups);
  const tableColumns = getFlatSegmentDataTableColumns(segmentColumns, !!results.time_comparison);

  const _isSingleSegmentedMetric = isSingleSegmentedMetric(results.headers, results.series);

  segmentsToDisplay.forEach((segment, idx) => {
    const segmentColor = getChartSegmentColorCombo(idx).primary;

    legendSegmentItems.push({
      id: segment.key,
      // improves readability for the legend when displaying results for single segmented metrics
      label: _isSingleSegmentedMetric ? segment.displayNamePath.slice(1).join(` / `) : segment.displayName,
      color: segmentColor,
      tableRow: getFlatSegmentTableRow(segment),
    });
  });

  let readOnlyLegendItems = [];
  if (canShowOtherSegment(bookmarkParams)) {
    readOnlyLegendItems = [
      {
        id: OTHER_SEGMENT_VALUE,
        label: `Other`,
        color: `none`,
      },
    ];
  }

  // generate special indices used by Insights to store selected legend segments
  const legendIdToIndexMap = getSortedKeyMapForFlatSegments(segmentsToDisplay);
  const legendIndexToId: string[] = [];
  Object.keys(legendIdToIndexMap).forEach((id) => (legendIndexToId[legendIdToIndexMap[id]] = id));

  // $overall total is used to calculate the "Other" segment.
  // The total in the center of the chart is always the sum of the slices (component limitation)
  let overallTotal: number;
  Object.keys(results.series).forEach((key) => {
    const overallNode = results.series[key][RESULTS_OVERALL_SUM_KEY];
    // $overall is only present when segmented
    if (overallNode) {
      overallTotal = (overallTotal || 0) + overallNode[`all`];
    } else {
      overallTotal = (overallTotal || 0) + results.series[key][`all`];
    }
  });

  return {
    legendSegmentItems,
    readOnlyLegendItems,
    legendIdToIndexMap,
    legendIndexToId,
    segmentLookup,
    overallTotal,
    tableColumns,
  };
}
